import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, DollarSign, TrendingUp, Activity } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import StatusChip from '../components/shared/StatusChip';
import InfoCard from '../../../components/common/InfoCard';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { pammInvestorService } from '../services/pammService';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  formatDateTime,
  getProfitColor,
} from '../utils';
import type { PAMMInvestor } from '../types';

export default function InvestorDetails() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [investor, setInvestor] = useState<PAMMInvestor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await pammInvestorService.getById(id);
      if (!data) throw new Error('Investor not found');
      setInvestor(data);
    } catch {
      setError('Failed to load investor details.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeader title="Investor Details" />
        <LoadingSkeleton type="detail" />
      </div>
    );
  }

  if (error || !investor) {
    return (
      <div className="space-y-4">
        <PageHeader title="Investor Details" />
        <PAMMErrorState
          message={error ?? 'Investor not found.'}
          onRetry={load}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-6">
      <PageHeader
        title={investor.name}
        description={`${investor.email} · ${investor.accountNumber}`}
      >
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
          Back
        </Button>
      </PageHeader>

      {/* Status + KYC */}
      <div className="flex items-center gap-3">
        <StatusChip status={investor.status} />
        <StatusChip status={investor.kycStatus} />
        <span className="text-xs text-muted-foreground">
          {investor.country}
        </span>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Invested Amount"
          value={formatCurrency(investor.investedAmount)}
          icon={DollarSign}
        />
        <StatsCard
          title="Current Value"
          value={formatCurrency(investor.currentValue)}
          icon={Activity}
        />
        <StatsCard
          title="Profit/Loss"
          value={formatCurrency(investor.profit)}
          icon={TrendingUp}
        />
        <StatsCard
          title="Allocation %"
          value={`${investor.allocationPercentage.toFixed(2)}%`}
          icon={Activity}
        />
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoCard
          title="Personal Information"
          fields={[
            { label: 'Full Name', value: investor.name },
            { label: 'Email', value: investor.email },
            { label: 'Phone', value: investor.phone ?? '—' },
            { label: 'Country', value: investor.country },
            {
              label: 'Account Number',
              value: (
                <span className="font-mono">{investor.accountNumber}</span>
              ),
            },
            {
              label: 'KYC Status',
              value: <StatusChip status={investor.kycStatus} size="sm" />,
            },
          ]}
        />
        <InfoCard
          title="Investment Details"
          fields={[
            { label: 'Fund', value: investor.fundName },
            {
              label: 'Invested Amount',
              value: formatCurrency(investor.investedAmount),
            },
            {
              label: 'Current Value',
              value: formatCurrency(investor.currentValue),
            },
            {
              label: 'Profit/Loss',
              value: (
                <span className={getProfitColor(investor.profit)}>
                  {formatCurrency(investor.profit)} (
                  {formatPercent(investor.profitPercentage)})
                </span>
              ),
            },
            {
              label: 'Allocation %',
              value: `${investor.allocationPercentage.toFixed(2)}%`,
            },
            { label: 'Joined', value: formatDate(investor.joinDate) },
            {
              label: 'Last Activity',
              value: formatDateTime(investor.lastActivity),
            },
            {
              label: 'Status',
              value: <StatusChip status={investor.status} size="sm" />,
            },
          ]}
        />
      </div>
    </div>
  );
}
